import { storage } from '@/lib/storage';
import { networkStatus } from '@/lib/network/status';
import { isNetworkError } from '@/lib/api/errors';

class ApiError extends Error {
  constructor(
    public status: number,
    message: string,
    public details?: unknown
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

class ApiClient {
  private baseUrl: string;

  constructor(baseUrl: string = '') {
    this.baseUrl = baseUrl;
  }

  private getToken(): string | null {
    // storage.getSync returns from localStorage (web) or the hydrated in-memory cache
    // (native). AuthProvider awaits hydrateStorage() before mounting so the cache is warm
    // before any API call lands here.
    return storage.getSync('token');
  }

  // Single fetch path so every REST call feeds the NetworkStatus store.
  // Gateway failures (502/503/504, network error) count as failures; any real
  // response from the origin (including 4xx / plain 500) counts as success because
  // the server is demonstrably reachable.
  private async rawFetch(path: string, init: RequestInit): Promise<Response> {
    try {
      const response = await fetch(`${this.baseUrl}${path}`, init);
      if (response.status === 502 || response.status === 503 || response.status === 504) {
        networkStatus.recordFailure();
      } else {
        networkStatus.recordSuccess();
      }
      return response;
    } catch (error) {
      if (isNetworkError(error)) networkStatus.recordFailure();
      throw error;
    }
  }

  private authHeaders(contentType = false): HeadersInit {
    const headers: Record<string, string> = {};
    const token = this.getToken();
    if (token) headers['Authorization'] = `Bearer ${token}`;
    if (contentType) headers['Content-Type'] = 'application/json';
    return headers;
  }

  private async handleResponse<T>(response: Response): Promise<T> {
    if (response.status === 401) {
      // Only redirect to login if we already had a token (session expired).
      // If there's no token, this is an auth call that failed — let the caller handle it.
      const hasToken = this.getToken();
      if (hasToken && typeof window !== 'undefined') {
        void storage.remove('token');
        window.location.href = '/login';
      }
      throw new ApiError(401, 'Unauthorized');
    }

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      // Support { title }, { message }, { errors: string[] } (ASP.NET Identity), or generic fallback
      const errorsArray: unknown = errorData.errors;
      const firstError =
        Array.isArray(errorsArray) && errorsArray.length > 0
          ? String(errorsArray[0])
          : null;
      throw new ApiError(
        response.status,
        errorData.title || errorData.message || firstError || 'Request failed',
        errorData
      );
    }

    if (response.status === 204) {
      return {} as T;
    }

    // Guard against HTML responses (e.g. a static-export fallback serving index.html when
    // the request missed the API origin). Without this, response.json() would throw the
    // opaque "Unexpected token '<', '<!doctype' is not valid JSON" error.
    const contentType = response.headers.get('content-type') ?? '';
    if (!contentType.toLowerCase().includes('application/json')) {
      throw new ApiError(
        response.status,
        `Expected JSON response but got "${contentType || 'unknown'}" — check that VITE_API_URL points at the API origin.`,
      );
    }

    return response.json();
  }

  async get<T>(path: string): Promise<T> {
    const response = await this.rawFetch(path, { method: 'GET', headers: this.authHeaders() });
    return this.handleResponse<T>(response);
  }

  async post<T, D = unknown>(path: string, data?: D): Promise<T> {
    const response = await this.rawFetch(path, {
      method: 'POST',
      headers: this.authHeaders(true),
      body: data ? JSON.stringify(data) : undefined,
    });
    return this.handleResponse<T>(response);
  }

  async put<T, D = unknown>(path: string, data: D): Promise<T> {
    const response = await this.rawFetch(path, {
      method: 'PUT',
      headers: this.authHeaders(true),
      body: JSON.stringify(data),
    });
    return this.handleResponse<T>(response);
  }

  async patch<T, D = unknown>(path: string, data: D): Promise<T> {
    const response = await this.rawFetch(path, {
      method: 'PATCH',
      headers: this.authHeaders(true),
      body: JSON.stringify(data),
    });
    return this.handleResponse<T>(response);
  }

  async delete<D = unknown>(path: string, data?: D): Promise<void> {
    const response = await this.rawFetch(path, {
      method: 'DELETE',
      headers: this.authHeaders(data !== undefined),
      body: data !== undefined ? JSON.stringify(data) : undefined,
    });
    await this.handleResponse<void>(response);
  }
}

export { ApiClient };

export const apiClient = new ApiClient(
  import.meta.env.VITE_API_URL ?? ''
);

export { ApiError };
